import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiTool, FiDownload, FiList, FiGrid } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';
import { exportExcel } from '../utils/exportExcel';
import OdooKanban from '../views/OdooKanban';
import '../views/odoo-views.css';

const STAGES = [
  { value: 'requested', label: 'Requested', color: '#8A93A3' },
  { value: 'materials', label: 'Materials Ready', color: '#B99A5B' },
  { value: 'in_progress', label: 'In Progress', color: '#3A6A96' },
  { value: 'approval', label: 'Approval', color: '#A87B2D' },
  { value: 'completed', label: 'Completed', color: '#3B7A4E' },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.value, s.label]));

export default function Assembly() {
  const me = useMe();
  const perm = findPerm(me.modules, 'assembly');
  const [rows, setRows] = useState(null);
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(false);
  const [view, setView] = useState('kanban');
  const [toast, setToast] = useState('');

  async function load() { try { setRows(await api('/assembly')); } catch (e) { setToast(e.message); } }
  useEffect(() => { load(); }, []);
  useEffect(() => { api('/store/options').then(setProducts).catch(() => {}); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }
  function fmt(ts) { return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }

  async function moveStage(a, stage) {
    if(a.stock_committed && stage!=='completed') return flash('Completed assemblies are locked because stock has been posted');
    setRows((rs) => rs.map((x) => x.id === a.id ? { ...x, stage } : x));
    try { await api(`/assembly/${a.id}/stage`, { method: 'PATCH', body: { stage } });
      flash(stage === 'completed' ? 'Completed — stock updated' : `Moved to ${STAGE_LABEL[stage]}`); load(); }
    catch (e) { flash(e.message); load(); }
  }

  function doExport() {
    exportExcel('Assemblies', [
      { label: 'Date', value: (a) => fmt(a.created_at) },
      { label: 'Product made', key: 'output_name' },
      { label: 'Qty', key: 'output_qty' },
      { label: 'Stage', value: (a) => STAGE_LABEL[a.stage] || a.stage },
      { label: 'Region', value: (a) => a.region || '' },
      { label: 'Components', key: 'component_count' },
      { label: 'By', key: 'by_name' },
      { label: 'Note', value: (a) => a.note || '' },
    ], rows || []);
  }

  function renderCard(a) {
    return (
      <>
        <div className="okb-card-title">{a.output_name || '—'} <span style={{ color: '#69727F', fontWeight: 400 }}>× {a.output_qty}</span></div>
        <div className="okb-card-row">
          {a.region && <span>{a.region}</span>}
          {a.region && <span>·</span>}
          <span>{a.component_count} component{a.component_count !== 1 ? 's' : ''}</span>
          {a.stock_committed && <span className="okb-badge" style={{ background: '#E9F3EC', color: '#3B7A4E' }}>stock posted</span>}
        </div>
        <div className="okb-card-row"><span>{a.by_name || '—'} · {fmt(a.created_at)}</span></div>
        {a.note && <div className="okb-card-row"><span style={{ fontStyle: 'italic' }}>{a.note}</span></div>}
      </>
    );
  }

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Assembly</div>
          <h1>Assembly</h1>
          <div className="count">{rows ? `${rows.length} assembl${rows.length !== 1 ? 'ies' : 'y'}` : 'Loading…'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="olv-viewswitch">
            <button className={'olv-vs' + (view === 'list' ? ' on' : '')} onClick={() => setView('list')} title="List"><FiList /></button>
            <button className={'olv-vs' + (view === 'kanban' ? ' on' : '')} onClick={() => setView('kanban')} title="Kanban"><FiGrid /></button>
          </div>
          <button className="btn btn-sm" onClick={doExport} disabled={!rows || rows.length === 0}><FiDownload /> Export</button>
          {perm.can_create && <button className="btn btn-dark" onClick={() => setModal(true)}><FiTool /> New assembly</button>}
        </div>
      </div>

      {!rows ? <div className="table-card"><div className="empty">Loading…</div></div> :
       rows.length === 0 ? <div className="table-card"><div className="empty"><h3>No assemblies yet</h3><p>Record an assembly — pick what you made and what you used.</p></div></div> :
       view === 'kanban' ? (
        <OdooKanban columns={STAGES} groupField="stage" records={rows}
          getKey={(a) => a.id} onCardClick={() => {}} renderCard={renderCard}
          onMove={moveStage} canMove={perm.can_edit} />
       ) : (
        <div className="table-card">
          <table className="data">
            <thead><tr>
              <th>Date</th><th>Product made</th><th style={{ textAlign: 'right' }}>Qty</th><th>Stage</th><th>Region</th>
              <th style={{ textAlign: 'right' }}>Components</th><th>By</th>
            </tr></thead>
            <tbody>
              {rows.map((a) => {
                const st = STAGES.find((s) => s.value === a.stage);
                return (
                  <tr key={a.id}>
                    <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmt(a.created_at)}</td>
                    <td style={{ fontWeight: 600 }}>{a.output_name || '—'}{a.note && <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>{a.note}</div>}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{a.output_qty}</td>
                    <td><span className="pill" style={{ background: (st?.color || '#8A93A3') + '22', color: st?.color || '#5A6472' }}>{st?.label || a.stage}</span></td>
                    <td style={{ color: 'var(--muted)' }}>{a.region || '—'}</td>
                    <td style={{ textAlign: 'right' }}><span className="pill" style={{ background: '#EEF0F3', color: '#5A6472' }}>{a.component_count}</span></td>
                    <td style={{ color: 'var(--muted)' }}>{a.by_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
       )}

      {modal && <AssemblyModal products={products} onClose={() => setModal(false)}
        onSaved={() => { setModal(false); flash('Assembly created'); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function AssemblyModal({ products, onClose, onSaved }) {
  const [output, setOutput] = useState('');
  const [outQty, setOutQty] = useState('1');
  const [region, setRegion] = useState('');
  const [note, setNote] = useState('');
  const [comps, setComps] = useState([{ product_id: '', free_text: '', qty: '' }]);
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);

  function setComp(i, k, v) { const n = comps.slice(); n[i] = { ...n[i], [k]: v }; setComps(n); }
  function addComp() { setComps([...comps, { product_id: '', free_text: '', qty: '' }]); }
  function rmComp(i) { setComps(comps.filter((_, idx) => idx !== i)); }

  async function save() {
    setError(''); setBusy(true);
    const components = comps
      .map((c) => ({ product_id: c.product_id || null, free_text: c.product_id ? null : c.free_text, qty: Number(c.qty) }));
    try {
      await api('/assembly', { method: 'POST', body: {
        output_product_id: output, output_qty: Number(outQty), region, note, components, stage: 'requested',
      } });
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <h2>New assembly</h2>
        <p className="page-desc" style={{ margin: '0 0 16px', fontSize: 13 }}>
          Pick what you're making and the components you'll use. It starts at <strong>Requested</strong> — move it across the board as work progresses. Stock updates when you mark it <strong>Completed</strong>.
        </p>
        <div className="grid2">
          <div className="field"><label>Product to assemble</label>
            <select value={output} onChange={(e) => setOutput(e.target.value)} autoFocus>
              <option value="">— Select finished product —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
          <div className="field"><label>Quantity to make</label>
            <input type="number" value={outQty} onChange={(e) => setOutQty(e.target.value)} placeholder="1" /></div>
        </div>
        <div className="grid2">
          <div className="field"><label>Region (optional)</label>
            <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. Kerala / Tamil Nadu" /></div>
          <div className="field"><label>Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any remark" /></div>
        </div>

        <label style={{ fontSize: 12.5, fontWeight: 600, color: '#4A453E' }}>Components (ad-hoc — differ every build)</label>
        <div className="targets-block">
          {comps.map((c, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr auto', gap: 8, marginBottom: 8, alignItems: 'start' }}>
              <div>
                <select value={c.product_id} onChange={(e) => setComp(i, 'product_id', e.target.value)} style={{ width: '100%' }}>
                  <option value="">— Pick component (or type below) —</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {!c.product_id && <input value={c.free_text} onChange={(e) => setComp(i, 'free_text', e.target.value)}
                  placeholder="…or free-text (non-stocked)" style={{ width: '100%', marginTop: 5, fontSize: 13 }} />}
              </div>
              <input type="number" value={c.qty} onChange={(e) => setComp(i, 'qty', e.target.value)} placeholder="Qty" />
              <button className="btn btn-sm btn-danger" onClick={() => rmComp(i)} title="Remove"><FiTrash2 /></button>
            </div>
          ))}
          <button className="btn btn-sm" onClick={addComp} style={{ marginTop: 2 }}><FiPlus /> Add component</button>
        </div>

        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !output}>{busy ? 'Saving…' : 'Create assembly'}</button>
        </div>
      </div>
    </div>
  );
}
